import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Data cutoff date for a test copy, moved from the Merchants list's Actions
// column (FIG-320). Behaviour is unchanged.

interface CutoffDateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchant: { id: string; name: string; data_cutoff_date?: string | null };
  onSaved: () => void;
}

export const CutoffDateDialog: React.FC<CutoffDateDialogProps> = ({ open, onOpenChange, merchant, onSaved }) => {
  const { toast } = useToast();
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setValue(merchant.data_cutoff_date || '');
  }, [open, merchant.data_cutoff_date]);

  const save = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('organisations')
        .update({ data_cutoff_date: value || null } as never)
        .eq('id', merchant.id);
      if (error) throw error;
      toast({
        title: 'Cutoff date updated',
        description: value
          ? `Data cutoff set to ${value}. Mappings will be filtered on the next report run.`
          : 'Data cutoff removed. All mappings from the source org are now active.',
      });
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update cutoff date',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Data Cutoff Date</DialogTitle>
          <DialogDescription>
            Set the cutoff date for <strong>{merchant.name}</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Label htmlFor="cutoff-edit-date">Cutoff Date</Label>
          <Input
            id="cutoff-edit-date"
            type="date"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
          />
          <p className="text-xs text-muted-foreground">Leave blank to remove the cutoff.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CutoffDateDialog;
